/**
 * El panel «Tu campaña» del asistente (29/10/2026), puro: lo que la frena y los avisos tempranos de la cuenta.
 *
 * - El panel NO pide nada: lee lo que ya calcularon los pasos (cada chequeo anota en la auditoría). Lo que la frena sale
 *   de lo guardado (`loQueFrena`, la misma regla que el paso más lejano) y, desde el primer chequeo, de sus bloqueos.
 * - Los avisos de la cuenta se ven desde «A quién», antes de armar nada: el estado de la cuenta, el saldo y el tope del
 *   mes. Salen de `GET /cuenta` (el contexto) con las reglas del backend: `cuentaDelModo` y `alertasSaldo`
 *   (`util/saldo.util.ts`: saldo menor que el umbral, gasto del 80 % del tope o más, tope alcanzado). Avisan; el que
 *   frena de verdad es el chequeo.
 * - En «A quién», lo elegido que todavía no se guardó se muestra con su cuenta y «Se guarda con «Siguiente»»: decía
 *   «Todavía no elegiste a quiénes» con «Todos tus clientes» elegido y 12 que pueden recibir en la pantalla. Lo que «Siguiente»
 *   guardaría se compara con lo guardado (`mismaAudiencia`).
 */

import type { AudienciaEntrada, AudienciaResumen, Chequeo, ConfigCuenta, CuentaPublica, FiltroAudienciaPublico, FiltroCompras, ModoTransporte } from "../types";
import type { FrenoDelAsistente, PasoAsistente } from "./chequeo";
import { BLOQUEO, tituloBloqueo } from "./textos";
import { cuentaDelModo, textoMarcaFrenada } from "./conexion";
import { FILTRO_COMPRAS_VACIO, filtroComprasDe, filtroParaMandar } from "./compras";
import { EXCLUIR_VACIO, excluirDeAudiencia, excluirParaMandar } from "./exclusiones";
import { cuandoSaleEnPalabras, type CuandoDelEnvio } from "./vasAMandar";
import { usd } from "./formato";
import { TEXTO_ACTIVIDAD } from "../../publico/logic/publico";

const RUTA_CONFIG = "/activa/oficial/configuracion";

/** El nombre de cada paso, como lo dicen el stepper, el título del paso y el panel. */
export const NOMBRE_DEL_PASO: Record<PasoAsistente, string> = {
  audiencia: "A quién", mensaje: "Qué les decís", cuando: "Cuándo", revisar: "Revisar y mandar",
};

/** Un renglón de «Lo que la frena» o de los avisos: qué pasa y, si se arregla en un paso o en otra pantalla, dónde. */
export type RenglonDelPanel = { texto: string; paso?: PasoAsistente; ruta?: string; label?: string };

/** Lo que frena la campaña: lo primero que le falta a lo guardado y los bloqueos del último chequeo. */
export function frenosDeLaCampana(freno: FrenoDelAsistente | null, chequeo: Pick<Chequeo, "bloqueos" | "saldo" | "gruposEnOtraCampana"> | null): RenglonDelPanel[] {
  const out: RenglonDelPanel[] = freno ? [{ texto: freno.motivo, paso: freno.arreglo }] : [];
  for (const code of chequeo?.bloqueos || []) {
    const accion = BLOQUEO[code]?.accion;
    out.push({
      texto: tituloBloqueo(code, { faltanteUSD: chequeo?.saldo?.faltanteUSD, comprometidoUSD: chequeo?.saldo?.comprometidoUSD, gruposEnOtraCampana: chequeo?.gruposEnOtraCampana }),
      ...(accion?.paso ? { paso: accion.paso } : accion?.ruta ? { ruta: accion.ruta, label: accion.label } : {}),
    });
  }
  return out;
}

/**
 * Los avisos de la cuenta, desde el primer paso: la cuenta sin conectar en el modo de hoy, la marca frenada o de
 * demostración, el saldo bajo y el tope del mes. Sin la cuenta (todavía no llegó), ninguno: no se avisa lo que no se sabe.
 */
export function avisosDeLaCuenta(cuenta: CuentaPublica | null, config: Pick<ConfigCuenta, "umbralSaldoBajoUSD" | "topeMensualUSD"> | null, modo: ModoTransporte | null): RenglonDelPanel[] {
  if (!cuenta) return [];
  const out: RenglonDelPanel[] = [];
  const aCuenta = { ruta: `${RUTA_CONFIG}?seccion=cuenta`, label: "Ir a Configuración" };
  const aSaldo = { ruta: `${RUTA_CONFIG}?seccion=saldo` };
  if (!cuentaDelModo(cuenta, modo)) out.push({ texto: "La cuenta de WhatsApp no está conectada: sin eso no sale ningún mensaje.", ...aCuenta });
  // el asistente es del equipo: el texto con el motivo del freno
  const frenada = textoMarcaFrenada(cuenta, true);
  if (frenada) out.push({ texto: `${frenada.titulo}: ningún mensaje sale hasta que alguien la reanude.`, ...aCuenta, label: "Ver por qué" });
  if (modo === "twilio" && cuenta.marcaDemoBloqueada) out.push({ texto: "Es la marca de demostración: no se manda con credenciales reales." });
  const saldo = Number(cuenta.saldoUSD);
  const umbral = Number(config?.umbralSaldoBajoUSD);
  if (Number.isFinite(saldo) && Number.isFinite(umbral) && saldo < umbral) {
    out.push({ texto: saldo > 0 ? `Te quedan ${usd(saldo)} de saldo.` : "No tenés saldo: sin saldo no sale ningún mensaje.", ...aSaldo, label: "Cargar saldo" });
  }
  const tope = config?.topeMensualUSD;
  const gasto = Number(cuenta.gastoMes?.usd) || 0;
  if (typeof tope === "number" && Number.isFinite(tope) && tope > 0) {
    if (gasto >= tope) out.push({ texto: `Llegaste al tope de gasto del mes (${usd(tope)}): hasta subirlo, no sale nada.`, ...aSaldo, label: "Ver tope" });
    else if (gasto >= 0.8 * tope) out.push({ texto: `Llevás ${usd(gasto)} de los ${usd(tope)} del tope de gasto del mes.`, ...aSaldo, label: "Ver tope" });
  }
  return out;
}

/**
 * Cuándo sale, en una línea y con la misma función que «Vas a mandar» (`cuandoSaleEnPalabras`): con «Ahora» y la franja
 * cerrada según el último chequeo, cuándo abre. Antes decía «Ahora, apenas confirmes» mientras la tarjeta decía «Apenas
 * abra la franja». Sin elegir, lo que falta.
 */
export function textoCuandoSale(c: CuandoDelEnvio, chequeo: Pick<Chequeo, "enFranjaAhora" | "proximaApertura"> | null = null): string {
  const texto = cuandoSaleEnPalabras(c, chequeo);
  if (texto) return texto;
  return c.modo === null ? "Todavía no elegiste si sale ahora o la programás" : "Programada: falta la fecha y la hora";
}

/** Lo elegido en «A quién»: lo que guardaría «Siguiente», en palabras, y cuántos pueden recibir según la cuenta de la pestaña (null si no contó). */
export type AudienciaEnPantalla = { entrada: AudienciaEntrada; texto: string; pueden: number | null };

/**
 * El segmento y la actividad de «Todos tus clientes», como los dice el nombre que guarda el backend: «todos los
 * segmentos», «En riesgo · respondió por WhatsApp». `segmentos`: los del Público (sin ellos, el id).
 */
export function detalleDelFiltroPublico(f: FiltroAudienciaPublico, segmentos: { id: string; nombre: string }[]): string[] {
  const segmento = !f.segmento ? "todos los segmentos"
    : f.segmento === "sin" ? "sin segmento"
    : segmentos.find((s) => s.id === f.segmento)?.nombre || f.segmento;
  const actividad = f.actividad ? (TEXTO_ACTIVIDAD as Record<string, string>)[f.actividad] || f.actividad : "";
  return [segmento, actividad && `${actividad.charAt(0).toLowerCase()}${actividad.slice(1)}`].filter(Boolean);
}

/** El filtro «Por compras» que cuenta, en un texto para comparar: "" sin filtro; las sucursales, en cualquier orden. */
function claveCompras(f: Partial<FiltroCompras> | null | undefined): string {
  const x = filtroParaMandar(filtroComprasDe(f));
  if (!x) return "";
  return (Object.keys(FILTRO_COMPRAS_VACIO) as (keyof FiltroCompras)[])
    .map((k) => JSON.stringify(k === "sucursales" ? [...x.sucursales].sort() : x[k]))
    .join("|");
}

/** Los mismos ids, en cualquier orden. */
function mismosIds(a: readonly string[], b: readonly string[]): boolean {
  const x = [...a].sort();
  const y = [...b].sort();
  return x.length === y.length && x.every((id, i) => id === y[i]);
}

/**
 * ¿«Siguiente» guardaría lo mismo que la campaña ya tiene? Lo elegido en «A quién» (lo que se mandaría) contra la
 * audiencia guardada: el tipo, lo elegido en cada uno, el filtro «Por compras» y las exclusiones. Sin guardada, no.
 */
export function mismaAudiencia(e: AudienciaEntrada, g: AudienciaResumen | null | undefined): boolean {
  if (!g || (g.tipo || "activa") !== (e.tipo ?? "activa")) return false;
  const ex = excluirParaMandar(e.excluir) ?? EXCLUIR_VACIO;
  const exGuardada = excluirDeAudiencia(g.excluir);
  if (!mismosIds(ex.campanas, exGuardada.campanas) || !mismosIds(ex.listas, exGuardada.listas)) return false;
  if (e.tipo === "publico") {
    const f = g.filtroPublico;
    return e.filtroPublico.segmento === (f?.segmento || "") && e.filtroPublico.actividad === (f?.actividad || "")
      && (e.filtroPublico.lista || "") === (f?.lista || "") && claveCompras(e.filtroCompras) === claveCompras(g.filtroCompras);
  }
  if (e.tipo === "base") return e.publicoId === g.publicoId && claveCompras(e.filtroCompras) === claveCompras(g.filtroCompras);
  return e.campanaActivaId === g.campanaActivaId && mismosIds(e.grupos, g.grupos || []);
}

/**
 * ¿Dos audiencias guardadas son la misma? Lo que decide a quién le llega: el tipo, lo elegido en cada uno, el filtro «Por
 * compras» y las exclusiones (el nombre no: el backend lo vuelve a armar). Sirve para saber si otra pestaña la cambió.
 */
export function mismaAudienciaGuardada(a: AudienciaResumen | null | undefined, b: AudienciaResumen | null | undefined): boolean {
  if (!a || !b) return !a && !b;
  const fa = a.filtroPublico;
  const fb = b.filtroPublico;
  const ea = excluirDeAudiencia(a.excluir);
  const eb = excluirDeAudiencia(b.excluir);
  return (a.tipo || "activa") === (b.tipo || "activa") && (a.campanaActivaId || "") === (b.campanaActivaId || "")
    && mismosIds(a.grupos || [], b.grupos || []) && (a.publicoId || "") === (b.publicoId || "")
    && (fa?.segmento || "") === (fb?.segmento || "") && (fa?.actividad || "") === (fb?.actividad || "") && (fa?.lista || "") === (fb?.lista || "")
    && claveCompras(a.filtroCompras) === claveCompras(b.filtroCompras)
    && mismosIds(ea.campanas, eb.campanas) && mismosIds(ea.listas, eb.listas);
}

/** Lo elegido sin guardar, con la forma de lo guardado: «Lo que la frena» mira lo guardado (`loQueFrena`). */
export function comoGuardada(e: AudienciaEntrada): AudienciaResumen {
  const vacia: AudienciaResumen = { tipo: "activa", campanaActivaId: "", nombre: "", grupos: [], publicoId: "", publicoTipo: null };
  if (e.tipo === "publico") return { ...vacia, tipo: "publico", filtroPublico: e.filtroPublico };
  if (e.tipo === "base") return { ...vacia, tipo: "base", publicoId: e.publicoId };
  return { ...vacia, campanaActivaId: e.campanaActivaId, grupos: e.grupos };
}
